// hooks/useTaskForm.ts
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useState } from 'react';
import { SubmitHandler, useForm, UseFormSetValue } from 'react-hook-form';
import { z } from 'zod';

import {
  getStringValue,
  hoursAndMinutesToSeconds,
} from '@/utils/dataNormalizers';
import {
  getGroupDisplayName,
  getTaskDisplayName,
  getUserDisplayName,
} from '@/utils/displayUtils';
import { useDebounced } from '@/hooks/ui/useDebounced';
import { useInitializationFlag } from './useInitializationFlag';
import { useTaskFormData } from './useTaskFormData';

export type TaskFormSetValue = UseFormSetValue<TaskFormData>;

const apiFileSchema = z.object({
  name: z.string().min(1),
  content: z.string().min(1),
  targetField: z.string().optional(),
});
export type ApiFile = z.infer<typeof apiFileSchema>;

export type ChecklistApiNode = {
  title: string;
  isComplete: boolean;
  members: number[];
  attachments: number[];
  files: ApiFile[];
  items: ChecklistApiNode[];
};

export const checklistApiNodeSchema: z.ZodType<ChecklistApiNode> = z.lazy(() =>
  z.object({
    title: z.string().min(1),
    isComplete: z.boolean().default(false),
    members: z.array(z.number()).default([]),
    attachments: z.array(z.number()).default([]),
    files: z.array(apiFileSchema).default([]),
    items: z.array(checklistApiNodeSchema).default([]),
  })
);

const fieldFilesSchema = z.object({
  field: z.string().min(1),
  entity: z.string().optional(),
  entityId: z.number().optional(),
  files: z.array(apiFileSchema).min(1),
});
export type FieldFilesPayload = z.infer<typeof fieldFilesSchema>;

// =========================
// Recurrence
// =========================
const recurrenceSchema = z.object({
  frequency: z.enum(['DAILY', 'WEEKLY', 'MONTHLY']),
  interval: z.number().int().min(1).max(365).optional(),
  weekDays: z.array(z.number().int().min(1).max(7)).optional(),
  monthDay: z.number().int().min(1).max(31).optional(),
  startDate: z.string().min(1),
  endDate: z.string().min(1).optional(),
  creatorId: z.number().optional(),
  responsibleId: z.number().optional(),
  sourceTaskId: z.number().optional(),
  timeOfDay: z.string().optional(),
});

export type RecurrencePayload = z.infer<typeof recurrenceSchema>;

export type TaskApiPayload = {
  TITLE: string;
  DESCRIPTION?: string;

  RESPONSIBLE_ID: number;

  GROUP_ID?: number | null;
  PARENT_ID?: number | null;

  DEADLINE?: string;
  REMIND_AT?: string;

  ACCOMPLICES?: number[];
  AUDITORS?: number[];

  TAGS?: string[];
  UF_CRM_TASK?: string[];

  TEMPLATE_ID?: number;
  CREATED_BY?: number;

  TIME_ESTIMATE?: number;

  PRIORITY?: number | null;
  ADD_IN_REPORT?: Yn;
  ALLOW_CHANGE_DEADLINE?: Yn;
  ALLOW_TIME_TRACKING?: Yn;
  TASK_CONTROL?: Yn;

  saveAsTemplate?: boolean;

  checklists?: ChecklistApiNode[];

  recurrence?: RecurrencePayload;

  files?: ApiFile[];
  fieldFiles?: FieldFilesPayload[];

  isMeeting?: boolean;
};

type Yn = 'Y' | 'N';
const toYN = (v: boolean | undefined | null): Yn => (v ? 'Y' : 'N');

export const taskFormSchema = z.object({
  TITLE: z.string().min(1, 'Название задачи обязательно'),
  DESCRIPTION: z.string().optional(),

  RESPONSIBLE_ID: z.number().min(1, 'ID ответственного обязательно'),

  GROUP_ID: z.number().nullable().optional(),
  PARENT_ID: z.number().nullable().optional(),

  DEADLINE: z.string().optional(),
  REMIND_AT: z.string().optional(),

  ACCOMPLICES: z.array(z.number()).optional(),
  AUDITORS: z.array(z.number()).optional(),

  TAGS: z.array(z.string()).optional(),
  UF_CRM_TASK: z.array(z.string()).optional(),

  TEMPLATE_ID: z.number().optional(),
  CREATED_BY: z.number().optional(),

  TIME_ESTIMATE_HOURS: z.number().min(0).max(1000).optional(),
  TIME_ESTIMATE_MINUTES: z.number().min(0).max(59).optional(),

  checklists: z.array(checklistApiNodeSchema).optional(),
  CHECKLIST: z.unknown().optional(),

  recurrence: recurrenceSchema.optional(),

  files: z.array(apiFileSchema).optional(),
  fieldFiles: z.array(fieldFilesSchema).optional(),

  isMeeting: z.boolean().optional(),

  PRIORITY: z.number().nullable().optional(),

  CONTROL_AFTER_FINISH: z.boolean().optional(),

  ALLOW_CHANGE_DEADLINE: z.boolean().optional(),
  ALLOW_TIME_TRACKING: z.boolean().optional(),
  ADD_IN_REPORT: z.boolean().optional(),

  saveAsTemplate: z.boolean().optional(),
});

export type TaskFormData = z.infer<typeof taskFormSchema>;

function getDefaultFormValues(): TaskFormData {
  return {
    TITLE: '',
    DESCRIPTION: '',
    RESPONSIBLE_ID: 0,

    GROUP_ID: null,
    PARENT_ID: null,

    DEADLINE: '',
    REMIND_AT: '',

    ACCOMPLICES: [],
    AUDITORS: [],

    TAGS: [],
    UF_CRM_TASK: [],

    TEMPLATE_ID: undefined,
    CREATED_BY: undefined,

    TIME_ESTIMATE_HOURS: undefined,
    TIME_ESTIMATE_MINUTES: undefined,

    checklists: undefined,
    CHECKLIST: { checklists: [], items: [] } as unknown,

    recurrence: undefined,
    files: [],
    fieldFiles: [],

    isMeeting: false,

    PRIORITY: null,

    CONTROL_AFTER_FINISH: false,

    ALLOW_CHANGE_DEADLINE: true,
    ALLOW_TIME_TRACKING: true,
    ADD_IN_REPORT: false,

    saveAsTemplate: false,
  };
}

interface UseTaskFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: TaskApiPayload) => void | Promise<void>;
  mode: 'create' | 'edit';
  initialData?: ANY;
}

const getSmartContractKey = (contract: ANY): string => {
  const id = contract.ID;
  const entityTypeId = contract.EntityTypeID || 0;
  return `SC_${entityTypeId}_${id}`;
};

type FormChecklistMini = { id: number; title: string };
type FormChecklistItemMini = {
  id: number;
  checklistId: number;
  title: string;
  isComplete?: boolean;
  parentId: number | null;
  members?: number[];
  attachments?: number[];
  files?: ApiFile[];
};

type FormChecklistValueMini = {
  checklists: FormChecklistMini[];
  items: FormChecklistItemMini[];
};

function mapFormChecklistValueToApiChecklists(
  rawUnknown?: unknown
): ChecklistApiNode[] | undefined {
  if (!rawUnknown) {
    return undefined;
  }

  const raw = rawUnknown as FormChecklistValueMini;

  const roots = Array.isArray(raw?.checklists) ? raw.checklists : [];
  const flatItems = Array.isArray(raw?.items) ? raw.items : [];

  if (!roots.length) {
    return undefined;
  }

  // группируем items по checklistId
  const itemsByChecklist = new Map<number, FormChecklistItemMini[]>();
  for (const it of flatItems) {
    const cid = Number(it?.checklistId ?? 0);
    if (!Number.isFinite(cid) || cid <= 0) {
      continue;
    }
    const arr = itemsByChecklist.get(cid) ?? [];
    arr.push(it);
    itemsByChecklist.set(cid, arr);
  }

  // строим дерево для одного checklistId
  const buildTree = (items: FormChecklistItemMini[]): ChecklistApiNode[] => {
    const byParent = new Map<number | null, FormChecklistItemMini[]>();

    for (const it of items) {
      const pid =
        it.parentId === null || it.parentId === undefined
          ? null
          : Number(it.parentId);

      const arr = byParent.get(pid) ?? [];
      arr.push(it);
      byParent.set(pid, arr);
    }

    // стабильная сортировка
    for (const [, arr] of byParent) {
      arr.sort((a, b) => Number(a.id) - Number(b.id));
    }

    const walk = (parentId: number | null): ChecklistApiNode[] => {
      const children = byParent.get(parentId) ?? [];
      return children
        .map((ch) => {
          const title = String(ch?.title ?? '').trim();
          if (!title) {
            return null;
          }

          const members = Array.isArray(ch.members)
            ? ch.members.map(Number).filter((n) => Number.isFinite(n) && n > 0)
            : [];

          const attachments = Array.isArray(ch.attachments)
            ? ch.attachments
                .map(Number)
                .filter((n) => Number.isFinite(n) && n > 0)
            : [];

          const files = Array.isArray(ch.files)
            ? ch.files
                .map((f) => ({
                  name: String(f?.name ?? '').trim(),
                  content: String(f?.content ?? '').trim(),
                  targetField: f?.targetField
                    ? String(f.targetField)
                    : undefined,
                }))
                .filter((f) => f.name && f.content)
            : [];

          const isComplete = Boolean(ch.isComplete);

          return {
            title,
            isComplete,
            members,
            attachments,
            files,
            items: walk(Number(ch.id)),
          } satisfies ChecklistApiNode;
        })
        .filter(Boolean) as ChecklistApiNode[];
    };

    return walk(null);
  };

  // каждый UI checklist (таб) -> один корневой узел API
  const apiChecklists: ChecklistApiNode[] = roots
    .map((cl, idx) => {
      const checklistId = Number(cl.id);
      const title = String(cl.title ?? '').trim() || `Чек-лист #${idx + 1}`;

      const listItems = itemsByChecklist.get(checklistId) ?? [];
      const nestedItems = buildTree(listItems);

      // Корень по контракту тоже "пункт", поэтому заполняем обязательные поля
      const rootNode: ChecklistApiNode = {
        title,
        isComplete: false,
        members: [],
        attachments: [],
        files: [],
        items: nestedItems,
      };

      // если совсем пусто — можно не отправлять
      const hasAnything =
        rootNode.title.trim().length > 0 ||
        rootNode.items.length > 0 ||
        rootNode.files.length > 0 ||
        rootNode.members.length > 0 ||
        rootNode.attachments.length > 0;

      return hasAnything ? rootNode : null;
    })
    .filter(Boolean) as ChecklistApiNode[];

  return apiChecklists.length ? apiChecklists : undefined;
}

function formToApi(data: TaskFormData): TaskApiPayload {
  function datetimeLocalToUTCISOString(
    datetimeLocal: string
  ): string | undefined {
    const v = String(datetimeLocal ?? '').trim();
    if (!v) {
      return undefined;
    }

    const m = v.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/);
    if (!m) {
      return undefined;
    }

    const yyyy = Number(m[1]);
    const mm = Number(m[2]);
    const dd = Number(m[3]);
    const HH = Number(m[4]);
    const MM = Number(m[5]);
    const SS = Number(m[6] ?? '0');

    const d = new Date(yyyy, mm - 1, dd, HH, MM, SS);
    if (Number.isNaN(d.getTime())) {
      return undefined;
    }

    return d.toISOString();
  }

  function normalizeDateToApiUtc(v: unknown): string | undefined {
    const s = String(v ?? '').trim();
    if (!s) {
      return undefined;
    }

    if (/[zZ]$/.test(s) || /[+-]\d{2}:\d{2}$/.test(s)) {
      return s;
    }

    return datetimeLocalToUTCISOString(s);
  }

  const timeEstimateSeconds = hoursAndMinutesToSeconds(
    data.TIME_ESTIMATE_HOURS,
    data.TIME_ESTIMATE_MINUTES
  );

  const TIME_ESTIMATE = Number.isFinite(timeEstimateSeconds)
    ? Math.max(0, timeEstimateSeconds)
    : 0;

  const apiChecklists =
    (data.checklists?.length ? data.checklists : undefined) ??
    mapFormChecklistValueToApiChecklists(data.CHECKLIST);

  const allowTimeTracking = data.ALLOW_TIME_TRACKING ?? true;
  const allowChangeDeadline = data.ALLOW_CHANGE_DEADLINE ?? true;
  const addInReport = data.ADD_IN_REPORT ?? false;

  const finalAllowTimeTracking = TIME_ESTIMATE > 0 ? allowTimeTracking : false;

  const payload: TaskApiPayload = {
    TITLE: String(data.TITLE ?? ''),
    DESCRIPTION: data.DESCRIPTION ? String(data.DESCRIPTION) : undefined,

    RESPONSIBLE_ID: Number(data.RESPONSIBLE_ID),

    GROUP_ID: data.GROUP_ID ?? null,
    PARENT_ID: data.PARENT_ID ?? null,

    DEADLINE: normalizeDateToApiUtc(data.DEADLINE),
    REMIND_AT: normalizeDateToApiUtc(data.REMIND_AT),

    ACCOMPLICES: Array.isArray(data.ACCOMPLICES) ? data.ACCOMPLICES : [],
    AUDITORS: Array.isArray(data.AUDITORS) ? data.AUDITORS : [],

    TAGS: Array.isArray(data.TAGS) ? data.TAGS : [],
    UF_CRM_TASK: Array.isArray(data.UF_CRM_TASK) ? data.UF_CRM_TASK : [],

    TEMPLATE_ID: data.TEMPLATE_ID,
    CREATED_BY: data.CREATED_BY,

    TIME_ESTIMATE,

    PRIORITY: data.PRIORITY ?? null,

    ALLOW_TIME_TRACKING: toYN(finalAllowTimeTracking),
    ALLOW_CHANGE_DEADLINE: toYN(allowChangeDeadline),
    ADD_IN_REPORT: toYN(addInReport),

    TASK_CONTROL: toYN(Boolean(data.CONTROL_AFTER_FINISH)),

    saveAsTemplate: Boolean(data.saveAsTemplate),

    isMeeting: Boolean(data.isMeeting),

    files: data.files?.length ? data.files : undefined,
    fieldFiles: data.fieldFiles?.length ? data.fieldFiles : undefined,

    recurrence: data.recurrence ? { ...data.recurrence } : undefined,
  };

  if (apiChecklists?.length) {
    payload.checklists = apiChecklists;
  }

  return payload;
}

const resolver = zodResolver(
  taskFormSchema
) as unknown as import('react-hook-form').Resolver<TaskFormData>;

export const useTaskForm = ({
  open,
  initialData,
  onSubmit,
  onClose,
}: UseTaskFormProps) => {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    trigger,
    getValues,
    formState: { errors },
  } = useForm<TaskFormData>({
    resolver,
    defaultValues: getDefaultFormValues(),
  });

  const { allUsers, allGroups, smartContracts, allTasks, relatedData } =
    useTaskFormData(initialData);

  const [userSearch, setUserSearch] = useState('');
  const [groupSearch, setGroupSearch] = useState('');
  const [accompliceSearch, setAccompliceSearch] = useState('');
  const [auditorSearch, setAuditorSearch] = useState('');
  const [parentTaskSearch, setParentTaskSearch] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [smartContractSearch, setSmartContractSearch] = useState('');

  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showGroupDropdown, setShowGroupDropdown] = useState(false);
  const [showAccompliceDropdown, setShowAccompliceDropdown] = useState(false);
  const [showAuditorDropdown, setShowAuditorDropdown] = useState(false);
  const [showParentTaskDropdown, setShowParentTaskDropdown] = useState(false);
  const [showSmartContractDropdown, setShowSmartContractDropdown] =
    useState(false);

  const debouncedUserSearch = useDebounced(userSearch, 400);
  const debouncedGroupSearch = useDebounced(groupSearch, 400);
  const debouncedAccompliceSearch = useDebounced(accompliceSearch, 400);
  const debouncedAuditorSearch = useDebounced(auditorSearch, 400);
  const debouncedParentTaskSearch = useDebounced(parentTaskSearch, 400);
  const debouncedSmartContractSearch = useDebounced(smartContractSearch, 400);

  const responsibleId = watch('RESPONSIBLE_ID');
  const accomplices = watch('ACCOMPLICES') || [];
  const auditors = watch('AUDITORS') || [];
  const tags = watch('TAGS') || [];
  const ufCrmTask = watch('UF_CRM_TASK') || [];

  const { resetInitialization } = useInitializationFlag();

  const filteredUsers = useMemo(
    () =>
      allUsers.filter((user) =>
        getUserDisplayName(user)
          .toLowerCase()
          .includes(debouncedUserSearch.toLowerCase())
      ),
    [allUsers, debouncedUserSearch]
  );

  const filteredGroups = useMemo(
    () =>
      allGroups.filter((group) =>
        getGroupDisplayName(group)
          .toLowerCase()
          .includes(debouncedGroupSearch.toLowerCase())
      ),
    [allGroups, debouncedGroupSearch]
  );

  const filteredAccompliceUsers = useMemo(
    () =>
      allUsers.filter(
        (user) =>
          getUserDisplayName(user)
            .toLowerCase()
            .includes(debouncedAccompliceSearch.toLowerCase()) &&
          !accomplices.includes(user.ID)
      ),
    [allUsers, debouncedAccompliceSearch, accomplices]
  );

  const filteredAuditorUsers = useMemo(
    () =>
      allUsers.filter(
        (user) =>
          getUserDisplayName(user)
            .toLowerCase()
            .includes(debouncedAuditorSearch.toLowerCase()) &&
          !auditors.includes(user.ID)
      ),
    [allUsers, debouncedAuditorSearch, auditors]
  );

  const filteredSmartContracts = useMemo(() => {
    if (!debouncedSmartContractSearch.trim()) {
      return smartContracts.filter(
        (contract) => !ufCrmTask.includes(getSmartContractKey(contract))
      );
    }

    return smartContracts.filter(
      (contract) =>
        getStringValue(contract.Title)
          .toLowerCase()
          .includes(debouncedSmartContractSearch.toLowerCase()) &&
        !ufCrmTask.includes(getSmartContractKey(contract))
    );
  }, [smartContracts, debouncedSmartContractSearch, ufCrmTask]);

  const filteredParentTasks = useMemo(
    () =>
      allTasks
        .filter((task) =>
          getTaskDisplayName(task)
            .toLowerCase()
            .includes(debouncedParentTaskSearch.toLowerCase())
        )
        .slice(0, 10),
    [allTasks, debouncedParentTaskSearch]
  );

  const handleFormSubmit: SubmitHandler<TaskFormData> = async (data) => {
    const payload = formToApi(data);
    await onSubmit(payload);
    onClose();
  };

  useEffect(() => {
    if (open) {
      return;
    }

    reset(getDefaultFormValues());

    setUserSearch('');
    setGroupSearch('');
    setAccompliceSearch('');
    setAuditorSearch('');
    setParentTaskSearch('');
    setTagInput('');
    setSmartContractSearch('');

    setShowUserDropdown(false);
    setShowGroupDropdown(false);
    setShowAccompliceDropdown(false);
    setShowAuditorDropdown(false);
    setShowParentTaskDropdown(false);
    setShowSmartContractDropdown(false);

    resetInitialization();
  }, [open, reset, resetInitialization]);

  const submit = handleSubmit(handleFormSubmit);

  return {
    register,
    submit,
    reset,
    trigger,
    getValues,
    setValue: setValue as TaskFormSetValue,
    watch,
    errors,

    allUsers,
    allGroups,
    allTasks,
    relatedData,
    smartContracts,

    filteredUsers,
    filteredGroups,
    filteredAccompliceUsers,
    filteredAuditorUsers,
    filteredSmartContracts,
    filteredParentTasks,

    userSearch,
    groupSearch,
    accompliceSearch,
    auditorSearch,
    parentTaskSearch,
    tagInput,
    smartContractSearch,

    showUserDropdown,
    showGroupDropdown,
    showAccompliceDropdown,
    showAuditorDropdown,
    showParentTaskDropdown,
    showSmartContractDropdown,

    responsibleId,
    accomplices,
    auditors,
    tags,
    ufCrmTask,

    setUserSearch,
    setGroupSearch,
    setAccompliceSearch,
    setAuditorSearch,
    setParentTaskSearch,
    setTagInput,
    setSmartContractSearch,
    setShowUserDropdown,
    setShowGroupDropdown,
    setShowAccompliceDropdown,
    setShowAuditorDropdown,
    setShowParentTaskDropdown,
    setShowSmartContractDropdown,
  };
};
